/**
 * Auth Zustand store following ADR-021 sliced store with immer middleware.
 *
 * State:  { token, user, isLoading, error }
 * Actions: { login, register, logout, clearError }
 *
 * Persist: token only (not user object) via localStorage.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { User, LoginCredentials, RegisterPayload } from '../types';
import { apiClient } from '../services/api.client';
import { logger } from '../services/logger.client';

const log = logger.child({ module: 'authStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface AuthState {
  token: string | null;
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export type AuthStore = AuthState & { actions: AuthActions };

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useAuthStore = create<AuthStore>()(
  persist(
    immer((set) => ({
      /* ---------- initial state ---------- */
      token: null,
      user: null,
      isLoading: false,
      error: null,

      /* ---------- actions ---------- */
      actions: {
        login: async (credentials: LoginCredentials) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            log.info('Attempting login', { email: credentials.email });

            const response = await apiClient<{
              success: boolean;
              data: { accessToken: string; user: User };
            }>('/api/v1/auth/login', {
              method: 'POST',
              body: JSON.stringify(credentials),
            });

            set((state) => {
              state.token = response.data.accessToken;
              state.user = response.data.user;
              state.isLoading = false;
            });

            log.info('Login successful', { userId: response.data.user.id });
          } catch (err) {
            const message =
              err instanceof Error ? err.message : 'Login failed';
            log.error('Login failed', { error: message });

            set((state) => {
              state.isLoading = false;
              state.error = message;
            });
          }
        },

        register: async (payload: RegisterPayload) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            log.info('Attempting registration', { email: payload.email });

            const response = await apiClient<{
              success: boolean;
              data: { accessToken: string; user: User };
            }>('/api/v1/auth/register', {
              method: 'POST',
              body: JSON.stringify(payload),
            });

            set((state) => {
              state.token = response.data.accessToken;
              state.user = response.data.user;
              state.isLoading = false;
            });

            log.info('Registration successful', { userId: response.data.user.id });
          } catch (err) {
            const message =
              err instanceof Error ? err.message : 'Registration failed';
            log.error('Registration failed', { error: message });

            set((state) => {
              state.isLoading = false;
              state.error = message;
            });
          }
        },

        logout: () => {
          log.info('Logging out');
          set((state) => {
            state.token = null;
            state.user = null;
            state.error = null;
          });
        },

        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },
      },
    })),
    {
      name: 'mmr-auth',
      storage: createJSONStorage(() => localStorage),
      /* Only persist the token -- never the full user object */
      partialize: (state) => ({
        token: state.token,
      }),
    },
  ),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks (ADR-021 wrapper-hooks pattern)                    */
/* ------------------------------------------------------------------ */

/** Stable reference to actions -- never causes a re-render. */
export function useAuthActions() {
  return useAuthStore((s) => s.actions);
}
